/**
 * Shared types and utilities for Firebase modules
 * This file contains common helpers and types used across all domain modules.
 *
 * NOTE: Firebase primitives (rootDb, fieldValue, storage, etc.) should be imported
 * directly from './firebase' in each module to avoid circular dependencies.
 */

import { getDefaultStore } from 'jotai'
import AsyncStorage from '@react-native-async-storage/async-storage'
import {
  organizationAtom,
  dbAtom,
  useSupabaseDeviceSettingAtom,
  SUPABASE_DEVICE_SETTING_KEY,
  DEFAULT_SUPABASE_SETTING,
} from '../AtomsProvider'
import { localDevSupabaseReads } from '../config/supabaseReads'

// ============================================================================
// Device-level Supabase Setting Cache
// ============================================================================

// Cache the device-toggle value to avoid async issues during startup. shouldUseSupabase()
// may be called before the Jotai atom (atomWithAsyncStorage) has hydrated via onMount, so we
// prime this cache directly from AsyncStorage at module load.
let cachedSupabaseSetting: boolean | null = null
let cacheInitialized = false
let cacheInitPromise: Promise<void> | null = null

const initializeCache = async (): Promise<void> => {
  if (cacheInitialized) return
  try {
    const stored = await AsyncStorage.getItem(SUPABASE_DEVICE_SETTING_KEY)
    if (stored !== null) {
      try {
        const parsed = JSON.parse(stored)
        if (typeof parsed === 'boolean') {
          cachedSupabaseSetting = parsed
        } else {
          console.warn('[shouldUseSupabase] Invalid stored value type, using default:', {
            stored,
            type: typeof parsed,
          })
          cachedSupabaseSetting = DEFAULT_SUPABASE_SETTING
        }
      } catch (parseError) {
        console.warn('[shouldUseSupabase] Corrupted JSON in AsyncStorage, using default:', {
          stored,
          parseError,
        })
        cachedSupabaseSetting = DEFAULT_SUPABASE_SETTING
        await AsyncStorage.removeItem(SUPABASE_DEVICE_SETTING_KEY)
      }
    } else {
      cachedSupabaseSetting = DEFAULT_SUPABASE_SETTING
    }
    cacheInitialized = true
  } catch (error) {
    console.warn('[shouldUseSupabase] Error reading from AsyncStorage, using default:', { error })
    cachedSupabaseSetting = DEFAULT_SUPABASE_SETTING
    cacheInitialized = true
  }
}

// Start loading immediately when the module is imported.
cacheInitPromise = initializeCache()
void cacheInitPromise

/**
 * Wait for the device-setting cache to be initialized. Useful before critical operations.
 */
export const waitForSupabaseSettingCache = (): Promise<void> => {
  return cacheInitPromise ?? Promise.resolve()
}

// ============================================================================
// Types
// ============================================================================

/**
 * Firestore-like reference object for compatibility with existing code
 */
export interface MockRef {
  id: string
  path: string
  update: () => Promise<never>
  delete: () => Promise<never>
  get: () => Promise<never>
  collection: (name: string) => { doc: () => MockRef }
}

/**
 * Firestore Timestamp-like object
 */
export interface TimestampLike {
  toDate: () => Date
  seconds: number
  nanoseconds: number
}

/**
 * Date range for queries
 */
export interface DateRange {
  startDate: Date
  endDate: Date
}

/**
 * Where condition for aggregation queries
 */
export interface WhereCondition {
  field: string
  operator: '<' | '<=' | '==' | '>=' | '>' | '!=' | 'array-contains' | 'array-contains-any' | 'in' | 'not-in'
  value: unknown
}

/**
 * Aggregation configuration
 */
export interface AggregationConfig {
  type: 'count' | 'sum' | 'average'
  field?: string
  alias: string
}

/**
 * Base document with common fields
 */
export interface BaseDocument {
  id?: string
  ref?: MockRef
  createdDate?: Date | TimestampLike
  updatedDate?: Date | TimestampLike
  createdBy?: string
  updatedBy?: string
  organizationId?: string
}

/**
 * Store information
 */
export interface Store {
  id: string
  name: string
  timezone?: string
  [key: string]: unknown
}

/**
 * Organization information
 */
export interface Organization {
  id: string
  name?: string
  useSupabaseForData?: boolean
  [key: string]: unknown
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the Jotai store instance
 */
export const getJotaiStore = () => getDefaultStore()

/**
 * Get the organization-scoped database reference
 */
export const getDb = () => {
  const jotaiStore = getDefaultStore()
  return jotaiStore.get(dbAtom)
}

/**
 * Get the current organization
 */
export const getOrganization = (): Organization | null => {
  try {
    const jotaiStore = getDefaultStore()
    return jotaiStore.get(organizationAtom) as Organization | null
  } catch (error) {
    console.warn('Error getting organization:', error)
    return null
  }
}

/**
 * Resolve the device-level Supabase override (the MiscScreen toggle).
 *
 * Reads the synchronous cache first (primed from AsyncStorage at module load), falling back to
 * the Jotai atom, then to the default (Firestore). This is a per-DEVICE developer/QA override
 * used to exercise the Supabase read path before an org is cut over.
 *
 * Exported for the ATL-888 live-rollback watch, which must know whether this session's Supabase
 * reads are attributable to the ORG flag or to this override — clearing the org flag cannot roll
 * back a device that is pinned to Supabase by its own toggle, and reloading it would drop the
 * cashier's screen to land in exactly the same place.
 */
export const getDeviceSupabaseOverride = (): boolean => {
  if (cachedSupabaseSetting !== null) {
    return cachedSupabaseSetting
  }
  try {
    return getDefaultStore().get(useSupabaseDeviceSettingAtom)
  } catch (error) {
    console.warn('[shouldUseSupabase] Cache and atom unavailable, using default (Firestore)', {
      error,
    })
    return DEFAULT_SUPABASE_SETTING
  }
}

/**
 * Resolve the per-org read-cutover gate.
 *
 * `organization.useSupabaseForData` is the explicit per-org switch: an org reads from Supabase
 * only once this flag is turned on (the cutover). It defaults to false, so every org reads from
 * Firestore until explicitly flipped — even orgs whose baseline has been migrated.
 */
const getOrgSupabaseReadCutover = (): boolean => {
  try {
    return getOrganization()?.useSupabaseForData === true
  } catch {
    return false
  }
}

/**
 * Whether data READS should come from Supabase (vs Firestore) for the current session.
 *
 * Launch posture: reads default to Firestore. Supabase reads are enabled when ANY of:
 *  - the org has been cut over (`useSupabaseForData === true`), or
 *  - this device has the developer/QA override on (MiscScreen toggle), or
 *  - the local-dev switch is on (dev build + `EXPO_PUBLIC_SUPABASE_READS=true`) — this is
 *    dead code in production builds and never affects prod behavior.
 *
 * This gates the READ path only. Dual-WRITES to Supabase happen unconditionally regardless of
 * this value (see global/Firebase/*.ts), so flipping it is a safe, immediate kill-switch.
 *
 * @returns true → read from Supabase; false → read from Firestore (default)
 */
export const shouldUseSupabase = (): boolean => {
  return localDevSupabaseReads() || getDeviceSupabaseOverride() || getOrgSupabaseReadCutover()
}

/**
 * Update the synchronous device-setting cache when the MiscScreen toggle changes.
 */
export const updateSupabaseSettingCache = (value: boolean): void => {
  cachedSupabaseSetting = value
  cacheInitialized = true
}

/**
 * Create a mock Firestore ref for compatibility with existing code
 * @param collection - Collection name
 * @param id - Document ID
 * @returns Mock ref object
 */
export const createMockRef = (collectionName: string, id: string): MockRef => {
  const mockRef: MockRef = {
    id,
    path: `${collectionName}/${id}`,
    update: () => Promise.reject(new Error('Cannot update via Supabase ref - use API')),
    delete: () => Promise.reject(new Error('Cannot delete via Supabase ref - use API')),
    get: () => Promise.reject(new Error('Cannot get via Supabase ref - use API')),
    collection: (name: string) => ({
      doc: () => createMockRef(`${collectionName}/${id}/${name}`, 'auto-' + Date.now()),
    }),
  }
  return mockRef
}

/**
 * Convert ISO timestamp string to Firestore Timestamp-like object
 * @param isoString - ISO timestamp string
 * @returns Object with toDate() method, or null if input is falsy
 */
export const convertTimestamp = (isoString: string | null | undefined): TimestampLike | null => {
  if (!isoString) return null
  const date = new Date(isoString)
  return {
    toDate: () => date,
    seconds: Math.floor(date.getTime() / 1000),
    nanoseconds: (date.getTime() % 1000) * 1000000,
  }
}

/**
 * Timestamp fields commonly found in documents
 */
const TIMESTAMP_FIELDS = [
  'date',
  'saleDate',
  'supplyDate',
  'createdDate',
  'createdAt',
  'updatedAt',
  'lastChangeAt',
  'registrationDate',
  'lastPurchaseDate',
  'paymentDate',
] as const

/**
 * Transform API response item to Firestore-like format
 * @param item - Item from API response
 * @param collection - Collection name for ref
 * @returns Item in Firestore format with ref and converted timestamps
 */
export const transformApiItem = <T extends Record<string, unknown>>(
  item: T | null | undefined,
  collection: string
): (T & { ref: MockRef }) | null => {
  if (!item) return null

  const transformed = { ...item } as T & { ref: MockRef }

  // Add ref if not present
  if (!transformed.ref && transformed.id) {
    transformed.ref = createMockRef(collection, transformed.id as string)
  }

  // Convert timestamp fields
  TIMESTAMP_FIELDS.forEach((field) => {
    const value = transformed[field]
    if (value && typeof value === 'string') {
      (transformed as Record<string, unknown>)[field] = convertTimestamp(value)
    }
  })

  return transformed
}

/**
 * Convert a Firestore date (could be Timestamp or Date) to Date object
 */
export const toDate = (value: Date | TimestampLike | string | null | undefined): Date | null => {
  if (!value) return null
  if (value instanceof Date) return value
  if (typeof value === 'string') return new Date(value)
  if (typeof (value as TimestampLike).toDate === 'function') {
    return (value as TimestampLike).toDate()
  }
  return null
}

/**
 * Convert a Date to ISO string for API calls
 */
export const toISOString = (value: Date | TimestampLike | string | null | undefined): string | null => {
  const date = toDate(value)
  return date ? date.toISOString() : null
}

/**
 * Platform-safe DocumentSnapshot existence check.
 *
 * The two Firestore SDKs disagree on `exists`:
 *  - native (@react-native-firebase/firestore v22) exposes it as a METHOD: `snap.exists()`
 *  - web (firebase/compat) exposes it as a boolean PROPERTY: `snap.exists`
 * Calling the web property as a function throws "exists is not a function", which
 * broke every Firestore read on web after ATL-570 globally switched `.exists` →
 * `.exists()` for the v22 native bump. This guard handles both shapes.
 */
export const snapshotExists = (
  snapshot: { exists?: unknown } | null | undefined
): boolean => {
  if (!snapshot) return false
  const { exists } = snapshot
  return typeof exists === 'function'
    ? Boolean((exists as () => unknown).call(snapshot))
    : Boolean(exists)
}

/**
 * Recursively filter out undefined values from an object.
 * Firestore doesn't accept undefined values, so this is needed before writing to Firestore.
 * @param obj - Object to filter
 * @returns New object without undefined values
 */
export const filterUndefined = <T extends Record<string, unknown>>(
  obj: T,
  seen: WeakSet<object> = new WeakSet()
): Partial<T> => {
  // Detect circular reference to prevent stack overflow
  if (seen.has(obj)) {
    return {} as Partial<T>
  }
  seen.add(obj)

  const result: Record<string, unknown> = {}
  for (const key of Object.keys(obj)) {
    const value = obj[key]
    if (value === undefined) continue
    if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
      // Check if it's a Firestore Timestamp-like object
      if (typeof (value as TimestampLike).toDate === 'function') {
        result[key] = value
      } else if (Array.isArray(value)) {
        // Filter undefined from arrays and recursively clean nested objects
        result[key] = value
          .filter((item) => item !== undefined)
          .map((item) => {
            if (item !== null && typeof item === 'object' && !(item instanceof Date)) {
              if (typeof (item as TimestampLike).toDate === 'function') {
                return item
              }
              return filterUndefined(item as Record<string, unknown>, seen)
            }
            return item
          })
      } else {
        result[key] = filterUndefined(value as Record<string, unknown>, seen)
      }
    } else {
      result[key] = value
    }
  }
  return result as Partial<T>
}
